export const formatCurrencyValue = (value: number, currency: string) => {
    const result = Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency
    }).format(value);

    return result;
};

export const getFullPaymentAmount = (invoice: SpApi.Client.IInvoice): number => {
    return Number(invoice.dueTotal);
};

export const getFullPaymentAmountText = (invoice: SpApi.Client.IInvoice): string => {
    const fullPaymentAmount = getFullPaymentAmount(invoice);
    return formatCurrencyValue(fullPaymentAmount, invoice.currencyCode);
};

export const getPartialPaymentAmount = (invoice: SpApi.Client.IInvoice): number => {
    const nextInstallmentBalance = getNextInstallmentBalance(invoice);
    const overdueBalance = getOverdueBalance(invoice);
    return overdueBalance + nextInstallmentBalance;
};

export const getPartialPaymentAmountText = (invoice: SpApi.Client.IInvoice): string => {
    const partialPaymentAmount = getPartialPaymentAmount(invoice);
    return formatCurrencyValue(partialPaymentAmount, invoice.currencyCode);
};

export const getNextInstallment = (
    invoice: SpApi.Client.IInvoice
): SpApi.Client.IInvoiceInstallment | undefined => {
    const now = new Date().toISOString();

    const unpaidInstallmentsByDueDate = invoice.installments
        .filter(
            (installment) =>
                installment.invoiceInstallmentStatus !== 'paid-in-full' &&
                installment.dueDate >= now
        )
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

    const nextInstallment: SpApi.Client.IInvoiceInstallment | undefined =
        unpaidInstallmentsByDueDate[0];

    return nextInstallment;
};

export const getNextInstallmentBalance = (invoice: SpApi.Client.IInvoice): number => {
    const nextInstallment = getNextInstallment(invoice);

    if (!nextInstallment) {
        return 0;
    }

    const partiallyPaidInstallmentAmount = getPartiallyPaidInstallmentAmount(invoice);

    return (
        nextInstallment.amount -
        (nextInstallment.invoiceInstallmentStatus === 'partially-paid'
            ? partiallyPaidInstallmentAmount
            : 0)
    );
};

export const getOverdueBalance = (invoice: SpApi.Client.IInvoice): number => {
    const partiallyPaidInstallmentAmount = getPartiallyPaidInstallmentAmount(invoice);
    const overdueInstallments = getOverdueInstallments(invoice);

    const overdueBalance = overdueInstallments.reduce(
        (r, installment) =>
            r +
            installment.amount -
            (installment.invoiceInstallmentStatus === 'partially-paid'
                ? partiallyPaidInstallmentAmount
                : 0),
        0
    );

    return overdueBalance;
};

export const getOverdueInstallments = (
    invoice: SpApi.Client.IInvoice
): Array<SpApi.Client.IInvoiceInstallment> => {
    const now = new Date().toISOString();

    return invoice.installments.filter(
        (installment) =>
            installment.invoiceInstallmentStatus !== 'paid-in-full' && installment.dueDate < now
    );
};

export const getPartiallyPaidInstallmentAmount = (invoice: SpApi.Client.IInvoice): number => {
    const fullyPaidInstallments = invoice.installments.filter(
        (installment) => installment.invoiceInstallmentStatus === 'paid-in-full'
    );

    const fullyPaidInstallmentsAmount = fullyPaidInstallments.reduce(
        (r, x) => r + Number(x.amount),
        0
    );

    return Number(invoice.paidTotal) - fullyPaidInstallmentsAmount;
};

export const isPaymentAvailable = (invoice: SpApi.Client.IInvoice): boolean => {
    return (
        invoice.invoiceStatus !== 'paid-in-full' &&
        invoice.acceptedPaymentTypes.includes('credit-card')
    );
};

export const isAutoPaySetupAvailable = (invoice: SpApi.Client.IInvoice): boolean => {
    return (
        !invoice.creditCardOnFile &&
        invoice.installments.filter(
            (installment) => installment.invoiceInstallmentStatus !== 'paid-in-full'
        ).length > 1
    );
};
